/**
 * ShareContextMenu Component
 * 
 * Right-click context menu for text selection with share options.
 * Appears at cursor position when text is selected.
 */

import React, { forwardRef } from 'react';

const ShareContextMenu = forwardRef(({
  visible,
  position,
  onShare,
  onClose,
  hasMarkdown,
  onHighlight,
  isHighlighting = false,
  showHighlight = false
}, ref) => {
  if (!visible) return null;
  
  // Adjust position to stay within viewport
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const menuWidth = 200;
  const menuHeight = 120;
  
  const adjustedLeft = Math.min(position.x, viewportWidth - menuWidth - 10);
  const adjustedTop = Math.min(position.y, viewportHeight - menuHeight - 10);
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40"
        onClick={onClose}
        style={{ background: 'transparent' }}
      />
      
      {/* Context Menu */}
      <div
        ref={ref}
        className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-48"
        style={{
          top: `${adjustedTop}px`,
          left: `${adjustedLeft}px`,
        }}
      >
        {/* Highlight Option (only show on summary tab) */}
        {showHighlight && (
          <button
            onClick={() => {
              if (onHighlight) {
                onHighlight();
              }
            }}
            disabled={isHighlighting}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-yellow-900 flex items-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            <span className="font-medium text-yellow-700 dark:text-yellow-300">
              {isHighlighting ? 'Highlighting...' : 'Highlight Text'}
            </span>
          </button>
        )}

        {/* Share as Image Option */}
        <button
          onClick={onShare}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
          </svg>
          <div>
            <div className="font-medium">Share as Image</div>
            {hasMarkdown && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                ✨ Formatting preserved
              </div>
            )}
          </div>
        </button>

        {/* Copy Text Option */}
        <button
          onClick={() => {
            const selection = window.getSelection();
            if (selection.toString()) {
              navigator.clipboard.writeText(selection.toString());
            }
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span>Copy Text</span>
        </button>
        
        <hr className="my-1 border-gray-200 dark:border-gray-600" />
        
        {/* Cancel Option */}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-left text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Cancel</span>
        </button>
      </div>
    </>
  );
});

ShareContextMenu.displayName = 'ShareContextMenu';

export default ShareContextMenu;